import type { FormEvent, ReactNode } from 'react'

import { useState } from 'react'
import { toast } from 'sonner'
import { useClinic } from '@/lib/clinic/store'
import { addDays, addMonths, formatDate } from '@/lib/dates'
import { byDateDesc, treatmentNames } from '@/lib/clinic/selectors'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import FormField from '@/components/app/form-field'
import PatientPicker from '@/components/app/patient-picker'

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'

export interface FollowUpDialogOptions {
    mode: 'create' | 'edit' | 'reschedule'
    followUpId?: string
    patientId?: string
}

interface FollowUpDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    options: FollowUpDialogOptions
    onBookAppointment: (patientId: string, date: string) => void
}

const FollowUpDialog = ({ open, onOpenChange, options, onBookAppointment }: FollowUpDialogProps): ReactNode => {
    const { data, today, actions } = useClinic()
    const existing = options.followUpId ? data.followUps.find(item => item.id === options.followUpId) : undefined
    const mode = existing ? options.mode : 'create'

    const lastVisitOf = (patientId: string) => data.visits.filter(item => item.patientId === patientId).sort(byDateDesc)[0]

    const [patientId, setPatientId] = useState(existing?.patientId ?? options.patientId ?? '')
    const [dueDate, setDueDate] = useState(() => {
        if (existing && mode === 'reschedule') return existing.dueDate < today ? addDays(today, 1) : existing.dueDate
        return existing?.dueDate ?? addDays(today, 7)
    })
    const [treatment, setTreatment] = useState(() => {
        if (existing) return existing.treatment
        const visit = options.patientId ? lastVisitOf(options.patientId) : undefined
        return visit ? treatmentNames(visit.treatments) : ''
    })
    const [notes, setNotes] = useState(existing?.notes ?? '')
    const [reason, setReason] = useState('')
    const [bookAppointment, setBookAppointment] = useState(false)
    const [submitted, setSubmitted] = useState(false)

    const patient = data.patients.find(item => item.id === patientId)

    const errors = {
        patientId: !patientId ? 'Select a patient.' : '',
        dueDate: !dueDate ? 'Choose the returning date.' : mode !== 'edit' && dueDate < today ? 'Choose today or a later date.' : ''
    }

    const handlePatientChange = (value: string) => {
        setPatientId(value)
        if (!treatment) {
            const visit = lastVisitOf(value)
            if (visit) setTreatment(treatmentNames(visit.treatments))
        }
    }

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault()
        setSubmitted(true)
        if (errors.patientId || errors.dueDate) return

        if (mode === 'reschedule' && existing) {
            actions.rescheduleFollowUp(existing.id, dueDate, reason)
            toast.success('Follow-up rescheduled', { description: `${patient?.name ?? 'Patient'} now returns on ${formatDate(dueDate)}` })
        }
        else if (mode === 'edit' && existing) {
            actions.updateFollowUp(existing.id, { dueDate, treatment: treatment.trim(), notes: notes.trim() })
            toast.success('Follow-up updated')
        }
        else {
            const visit = lastVisitOf(patientId)
            actions.addFollowUp({
                patientId,
                visitId: visit?.id ?? null,
                treatment: treatment.trim() || 'Review visit',
                previousVisitDate: visit?.date ?? '',
                dueDate,
                notes: notes.trim(),
                status: 'pending'
            })
            toast.success('Follow-up added', { description: `${patient?.name ?? 'Patient'} is due on ${formatDate(dueDate)}` })
        }

        onOpenChange(false)
        if (bookAppointment) onBookAppointment(patientId, dueDate)
    }

    const quick = [
        { label: 'Tomorrow', value: addDays(today, 1) },
        { label: '1 week', value: addDays(today, 7) },
        { label: '2 weeks', value: addDays(today, 14) },
        { label: '1 month', value: addMonths(today, 1) },
        { label: '6 months', value: addMonths(today, 6) }
    ]

    const title = mode === 'reschedule' ? 'Reschedule follow-up' : mode === 'edit' ? 'Edit follow-up' : 'Add follow-up'

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='sm:max-w-lg'>
                <form onSubmit={handleSubmit} noValidate className='grid gap-5'>
                    <DialogHeader>
                        <DialogTitle>{title}</DialogTitle>
                        <DialogDescription>
                            {existing && patient
                                ? `${patient.name}, currently due ${formatDate(existing.dueDate)}.`
                                : 'Set the date the patient should come back.'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className='grid gap-4'>
                        {mode === 'create' && (
                            <FormField label='Patient' htmlFor='follow-up-patient' required error={submitted ? errors.patientId : ''}>
                                <PatientPicker id='follow-up-patient' value={patientId} onChange={handlePatientChange} invalid={submitted && Boolean(errors.patientId)} />
                            </FormField>
                        )}

                        <FormField label={mode === 'reschedule' ? 'New returning date' : 'Returning date'} htmlFor='follow-up-date' required error={submitted ? errors.dueDate : ''}>
                            <Input
                                id='follow-up-date'
                                type='date'
                                value={dueDate}
                                min={mode === 'edit' ? undefined : today}
                                onChange={event => setDueDate(event.target.value)}
                                aria-invalid={submitted && Boolean(errors.dueDate)}
                            />
                            <div className='flex flex-wrap gap-1.5'>
                                {quick.map(option => (
                                    <button
                                        key={option.label}
                                        type='button'
                                        onClick={() => setDueDate(option.value)}
                                        className='h-7 rounded-md border bg-card px-2 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none'
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </FormField>

                        {mode === 'reschedule'
                            ? (
                                <FormField label='Reason' htmlFor='follow-up-reason' hint='Saved in the reminder history'>
                                    <Input id='follow-up-reason' value={reason} onChange={event => setReason(event.target.value)} placeholder='Patient is travelling this week' />
                                </FormField>
                            )
                            : (
                                <>
                                    <FormField label='Last treatment' htmlFor='follow-up-treatment'>
                                        <Input id='follow-up-treatment' value={treatment} onChange={event => setTreatment(event.target.value)} placeholder='Root Canal Treatment (RCT)' />
                                    </FormField>

                                    <FormField label='Follow-up notes' htmlFor='follow-up-notes'>
                                        <Textarea id='follow-up-notes' value={notes} onChange={event => setNotes(event.target.value)} placeholder='What should happen at this visit' rows={3} />
                                    </FormField>
                                </>
                            )}

                        <div className='flex items-center gap-2'>
                            <Checkbox id='follow-up-book' checked={bookAppointment} onCheckedChange={checked => setBookAppointment(checked === true)} />
                            <Label htmlFor='follow-up-book' className='text-sm font-normal'>
                                Book an appointment for this date after saving
                            </Label>
                        </div>
                    </div>

                    <DialogFooter>
                        <DialogClose render={<Button type='button' variant='outline' />}>
                            Cancel
                        </DialogClose>
                        <Button type='submit'>
                            {mode === 'reschedule' ? 'Reschedule' : mode === 'edit' ? 'Save changes' : 'Add follow-up'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}

export default FollowUpDialog
